import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defer, NEVER, Observable, ReplaySubject, Subject } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { getHttpInterceptorConfig } from '@app/core/interceptors/classes/http-interceptor-params';

const MAX_CONCURRENT_REQUESTS = 4;

@Injectable({
  providedIn: 'root',
})
export class HttpQueueService {
  private readonly cancelPendingRequests$ = new Subject<void>();

  private readonly pendingRequests = new Set<ReplaySubject<void>>();
  private readonly activeRequests = new Set<ReplaySubject<void>>();

  private isCancelling = false;

  public enqueue(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    const interceptorConfig = getHttpInterceptorConfig(request);
    const isCancellable = !interceptorConfig?.toIgnoreQueueCancellation;

    return defer(() => {
      if (interceptorConfig?.toCancelPendingRequests) {
        this.cancelPendingRequests();
      }

      const item: ReplaySubject<void> = new ReplaySubject<void>(1);

      this.pendingRequests.add(item);
      this.runQueue();

      return item.pipe(
        switchMap(() => next(request)),
        takeUntil(isCancellable ? this.cancelPendingRequests$ : NEVER),
        finalize(() => this.release(item))
      );
    });
  }

  private cancelPendingRequests(): void {
    this.isCancelling = true;
    this.cancelPendingRequests$.next();
    this.isCancelling = false;

    this.runQueue();
  }

  private runQueue(): void {
    if (this.isCancelling) {
      return;
    }

    while (this.activeRequests.size < MAX_CONCURRENT_REQUESTS && this.pendingRequests.size) {
      const [item] = this.pendingRequests;

      this.pendingRequests.delete(item);
      this.activeRequests.add(item);

      item.next();
      item.complete();
    }
  }

  private release(item: ReplaySubject<void>): void {
    this.pendingRequests.delete(item);
    this.activeRequests.delete(item);

    this.runQueue();
  }
}
